import React, { useState } from 'react';

interface AssessmentData {
  patientName: string;
  dateOfBirth: string;
  assessmentDate: string;
  examiner: string;
  
  // 1. Allgemeiner Eindruck
  appearance: string[];
  contactBehavior: string[];
  language: string[];
  intelligence: string;
  
  // 2. Bewusstseins- und Orientierungsstörungen
  qualitativeConsciousness: string[];
  quantitativeConsciousness: string[];
  orientationDisorders: string[];
  
  // 3. Aufmerksamkeits- und Gedächtnisstörungen
  attentionMemory: string[];
  
  // 4. Formale Denkstörungen
  formalThinking: string[];
  
  // 5. Inhaltliche Denkstörungen
  fearsCompulsions: string[];
  delusionalContent: string[];
  formalDelusional: string[];
  
  // 6. Wahrnehmungsstörungen
  perceptionDisorders: string[];
  
  // 7. Ich-Störungen
  egoDisorders: string[];
  
  // 8. Affektive Störungen
  affectiveDisorders: string[];
  
  // 9. Störungen von Antrieb und Psychomotorik
  driveDisorders: string[];
  psychomotorDisorders: string[];
  
  // 10. Zirkadiane Besonderheiten
  circadianFeatures: string[];
  
  // 11. Weitere Zusatzmerkmale
  additionalFeatures: string[];
  
  additionalNotes: string;
}

const initialData: AssessmentData = {
  patientName: '',
  dateOfBirth: '',
  assessmentDate: new Date().toISOString().split('T')[0],
  examiner: '',
  appearance: [],
  contactBehavior: [],
  language: [],
  intelligence: '',
  qualitativeConsciousness: [],
  quantitativeConsciousness: [],
  orientationDisorders: [],
  attentionMemory: [],
  formalThinking: [],
  fearsCompulsions: [],
  delusionalContent: [],
  formalDelusional: [],
  perceptionDisorders: [],
  egoDisorders: [],
  affectiveDisorders: [],
  driveDisorders: [],
  psychomotorDisorders: [],
  circadianFeatures: [],
  additionalFeatures: [],
  additionalNotes: ''
};

const assessmentOptions = {
  appearance: [
    'Gepflegt',
    'Ungepflegt',
    'Auffällig gekleidet',
    'Unauffällig gekleidet'
  ],
  
  contactBehavior: [
    'Kooperativ',
    'Unkooperativ',
    'Distanziert',
    'Distanzlos',
    'Misstrauisch',
    'Freundlich',
    'Abweisend'
  ],
  
  language: [
    'Flüssig',
    'Stockend',
    'Verlangsamt',
    'Beschleunigt',
    'Leise',
    'Laut',
    'Monoton'
  ],
  
  intelligence: [
    'Unterdurchschnittlich',
    'Durchschnittlich',
    'Überdurchschnittlich'
  ],
  
  qualitativeConsciousness: [
    'Bewusstseinstrübung',
    'Bewusstseinseinengung',
    'Bewusstseinsverschiebung'
  ],
  
  quantitativeConsciousness: [
    'Somnolenz',
    'Sopor',
    'Koma'
  ],
  
  orientationDisorders: [
    'Zeitlich',
    'Örtlich',
    'Situativ',
    'Zur Person'
  ],
  
  attentionMemory: [
    'Auffassungsstörungen',
    'Konzentrationsstörungen',
    'Merkfähigkeitsstörungen',
    'Gedächtnisstörungen',
    'Konfabulationen',
    'Paramnesien (inkl. Wahn-Paramnesien)',
    'Déjà-vu',
    'Hypermnesien'
  ],
  
  formalThinking: [
    'Denkzerfahrenheit (Paralogik, Paragrammatismus, Begriffszerfall)',
    'Denkhemmung / Denkverlangsamung',
    'Gedankenabreißen / Gesperrtes Denken',
    'Ideenflucht',
    'Perseveration',
    'Grübeln',
    'Eingeengtes Denken',
    'Umständliches Denken',
    'Gedankendrängen',
    'Vorbeireden',
    'Neologismen'
  ],
  
  fearsCompulsions: [
    'Zwangsgedanken',
    'Zwangshandlungen',
    'Zwangsimpulse',
    'Phobien',
    'Hypochondrie',
    'Misstrauen'
  ],
  
  delusionalContent: [
    'Beziehungswahn',
    'Beeinträchtigungswahn',
    'Verfolgungswahn (inkl. Vergiftungswahn)',
    'Größenwahn',
    'Verarmungswahn',
    'Schuldwahn',
    'Nihilistischer Wahn',
    'Hypochondrischer Wahn',
    'Eifersuchtswahn'
  ],
  
  formalDelusional: [
    'Wahnwahrnehmung',
    'Wahnstimmung',
    'Wahndynamik',
    'Wahnarbeit',
    'Wahneinfall / Wahnidee',
    'Wahngedanken',
    'Wahnsystem',
    'Überwertige Idee'
  ],
  
  perceptionDisorders: [
    'Illusionäre Verkennungen',
    'Akoasmen',
    'Kommentierende Stimmen',
    'Dialogisierende Stimmen',
    'Imperative Stimmen',
    'Optische Halluzinationen',
    'Taktile Halluzinationen',
    'Zönästhesien',
    'Pseudohalluzinationen',
    'Hypnagoge Halluzinationen',
    'Geruchs- und Geschmackshalluzinationen',
    'Metamorphopsien (Mikropsie, Makropsie, Dysmorphopsie)'
  ],
  
  egoDisorders: [
    'Derealisation',
    'Depersonalisation',
    'Gedankenentzug',
    'Gedankeneingebung',
    'Gedankenausbreitung',
    'Andere Fremdbeeinflussungserlebnisse'
  ],
  
  affectiveDisorders: [
    'Affektarmut / Affektverflachung',
    'Affektstarre',
    'Affektlabilität',
    'Affektinkontinenz',
    'Ambivalenz',
    'Parathymie / Paramimie',
    'Gefühl der Gefühllosigkeit',
    'Insuffizienzgefühl',
    'Gesteigertes Selbstwertgefühl',
    'Euphorie',
    'Dysphorie',
    'Störung der Vitalgefühle',
    'Schuldgefühle',
    'Verarmungsgefühle',
    'Ratlos',
    'Deprimiert',
    'Hoffnungslos',
    'Ängstlich',
    'Gereizt',
    'Innerlich unruhig',
    'Klagsam/jammerig'
  ],
  
  driveDisorders: [
    'Antriebsarmut',
    'Antriebshemmung',
    'Antriebssteigerung (bis Antriebsenthemmung)'
  ],
  
  psychomotorDisorders: [
    'Motorische Unruhe',
    'Stupor',
    'Parakinesen (Verbigeration, Katalepsie, Echolalie, Flexibilitas cerea, Negativismus)',
    'Manierismen',
    'Theatralisches Verhalten',
    'Mutismus (elektiv oder total)',
    'Logorrhö'
  ],
  
  circadianFeatures: [
    'Morgentief',
    'Abends schlechter',
    'Abends besser'
  ],
  
  additionalFeatures: [
    'Beschleunigtes Denken',
    'Impulsivität',
    'Distanzlosigkeit',
    'Desorganisation',
    'Wortfindungsstörungen',
    'Gedankenlautwerden',
    'Beziehungsideen',
    'Angstanfälle (Panikattacken)',
    'Schamgefühle',
    'Körperbildstörung'
  ]
};

export default function PsychopathologicalAssessment() {
  const [data, setData] = useState<AssessmentData>(initialData);

  const updateField = (field: keyof AssessmentData, value: string) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const updateArrayField = (field: keyof AssessmentData, value: string, checked: boolean) => {
    setData(prev => {
      const currentArray = prev[field] as string[];
      if (checked) {
        return { ...prev, [field]: [...currentArray, value] };
      } else {
        return { ...prev, [field]: currentArray.filter(item => item !== value) };
      }
    });
  };

  const generateReport = () => {
    const parts = [];
    
    parts.push('PSYCHOPATHOLOGISCHER BEFUND');
    parts.push('═══════════════════════════════════════════════════════════════════');
    parts.push('');
    
    if (data.patientName) parts.push(`Patient: ${data.patientName}`);
    if (data.dateOfBirth) parts.push(`Geburtsdatum: ${data.dateOfBirth}`);
    if (data.assessmentDate) parts.push(`Untersuchungsdatum: ${data.assessmentDate}`);
    if (data.examiner) parts.push(`Untersucher: ${data.examiner}`);
    parts.push('');
    
    // 1. Allgemeiner Eindruck
    parts.push('1. ALLGEMEINER EINDRUCK');
    parts.push('───────────────────────────────────────────────────────────────────');
    if (data.appearance.length > 0) parts.push(`Äußeres Erscheinungsbild: ${data.appearance.join(', ')}`);
    if (data.contactBehavior.length > 0) parts.push(`Kontaktverhalten: ${data.contactBehavior.join(', ')}`);
    if (data.language.length > 0) parts.push(`Sprache: ${data.language.join(', ')}`);
    if (data.intelligence) parts.push(`Intelligenz: ${data.intelligence}`);
    parts.push('');
    
    // 2. Bewusstseins- und Orientierungsstörungen
    if (data.qualitativeConsciousness.length > 0 || data.quantitativeConsciousness.length > 0 || data.orientationDisorders.length > 0) {
      parts.push('2. BEWUSSTSEINS- UND ORIENTIERUNGSSTÖRUNGEN');
      parts.push('───────────────────────────────────────────────────────────────────');
      if (data.qualitativeConsciousness.length > 0) parts.push(`Qualitative Bewusstseinsstörungen: ${data.qualitativeConsciousness.join(', ')}`);
      if (data.quantitativeConsciousness.length > 0) parts.push(`Quantitative Bewusstseinsstörungen: ${data.quantitativeConsciousness.join(', ')}`);
      if (data.orientationDisorders.length > 0) parts.push(`Orientierungsstörungen (ZOSP): ${data.orientationDisorders.join(', ')}`);
      parts.push('');
    }
    
    // 3. Aufmerksamkeits- und Gedächtnisstörungen
    if (data.attentionMemory.length > 0) {
      parts.push('3. AUFMERKSAMKEITS- UND GEDÄCHTNISSTÖRUNGEN');
      parts.push('───────────────────────────────────────────────────────────────────');
      parts.push(data.attentionMemory.join(', '));
      parts.push('');
    }
    
    // 4. Formale Denkstörungen
    if (data.formalThinking.length > 0) {
      parts.push('4. FORMALE DENKSTÖRUNGEN');
      parts.push('───────────────────────────────────────────────────────────────────');
      parts.push(data.formalThinking.join(', '));
      parts.push('');
    }
    
    // 5. Inhaltliche Denkstörungen
    if (data.fearsCompulsions.length > 0 || data.delusionalContent.length > 0 || data.formalDelusional.length > 0) {
      parts.push('5. INHALTLICHE DENKSTÖRUNGEN');
      parts.push('───────────────────────────────────────────────────────────────────');
      if (data.fearsCompulsions.length > 0) parts.push(`Befürchtungen und Zwänge: ${data.fearsCompulsions.join(', ')}`);
      if (data.delusionalContent.length > 0) parts.push(`Wahninhalte: ${data.delusionalContent.join(', ')}`);
      if (data.formalDelusional.length > 0) parts.push(`Formale Wahnmerkmale: ${data.formalDelusional.join(', ')}`);
      parts.push('');
    }
    
    // 6. Wahrnehmungsstörungen
    if (data.perceptionDisorders.length > 0) {
      parts.push('6. WAHRNEHMUNGSSTÖRUNGEN');
      parts.push('───────────────────────────────────────────────────────────────────');
      parts.push(data.perceptionDisorders.join(', '));
      parts.push('');
    }
    
    // 7. Ich-Störungen
    if (data.egoDisorders.length > 0) {
      parts.push('7. ICH-STÖRUNGEN');
      parts.push('───────────────────────────────────────────────────────────────────');
      parts.push(data.egoDisorders.join(', '));
      parts.push('');
    }
    
    // 8. Affektive Störungen
    if (data.affectiveDisorders.length > 0) {
      parts.push('8. AFFEKTIVE STÖRUNGEN');
      parts.push('───────────────────────────────────────────────────────────────────');
      parts.push(data.affectiveDisorders.join(', '));
      parts.push('');
    }
    
    // 9. Störungen von Antrieb und Psychomotorik
    if (data.driveDisorders.length > 0 || data.psychomotorDisorders.length > 0) {
      parts.push('9. STÖRUNGEN VON ANTRIEB UND PSYCHOMOTORIK');
      parts.push('───────────────────────────────────────────────────────────────────');
      if (data.driveDisorders.length > 0) parts.push(`Antriebsstörungen: ${data.driveDisorders.join(', ')}`);
      if (data.psychomotorDisorders.length > 0) parts.push(`Psychomotorische Störungen: ${data.psychomotorDisorders.join(', ')}`);
      parts.push('');
    }
    
    // 10. Zirkadiane Besonderheiten
    if (data.circadianFeatures.length > 0) {
      parts.push('10. ZIRKADIANE BESONDERHEITEN');
      parts.push('───────────────────────────────────────────────────────────────────');
      parts.push(data.circadianFeatures.join(', '));
      parts.push('');
    }
    
    // 11. Weitere Zusatzmerkmale
    if (data.additionalFeatures.length > 0) {
      parts.push('11. WEITERE ZUSATZMERKMALE (NACH AMDP)');
      parts.push('───────────────────────────────────────────────────────────────────');
      parts.push(data.additionalFeatures.join(', '));
      parts.push('');
    }
    
    // Zusätzliche Notizen
    if (data.additionalNotes) {
      parts.push('ZUSÄTZLICHE NOTIZEN');
      parts.push('───────────────────────────────────────────────────────────────────');
      parts.push(data.additionalNotes);
      parts.push('');
    }
    
    parts.push('───────────────────────────────────────────────────────────────────');
    parts.push(`Erstellt am: ${new Date().toLocaleDateString('de-DE')}`);
    if (data.examiner) parts.push(`Untersucher: ${data.examiner}`);
    
    return parts.join('\\n');
  };

  const copyToClipboard = () => {
    const report = generateReport();
    navigator.clipboard.writeText(report).then(() => {
      alert('Text wurde in die Zwischenablage kopiert!');
    });
  };

  const openGPTChatbot = () => {
    const gptLink = "https://chatgpt.com/g/g-6909ed1010848191a322f09581b63c8d-krankenbruder-ch-writing-assistent";
    window.open(gptLink, '_blank');
  };

  const CheckboxGroup = ({ title, options, field }: { title: string; options: string[]; field: keyof AssessmentData }) => (
    <div style={{ border: '1px solid #ccc', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
      <h3 style={{ color: '#1e40af', marginBottom: '12px', fontSize: '18px', fontWeight: 'bold' }}>{title}</h3>
      <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
        {options.map((option) => (
          <div key={option} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <input
              type="checkbox"
              id={`${field}-${option}`}
              checked={(data[field] as string[]).includes(option)}
              onChange={(e) => updateArrayField(field, option, e.target.checked)}
              style={{ marginRight: '8px' }}
            />
            <label htmlFor={`${field}-${option}`} style={{ fontSize: '14px', cursor: 'pointer' }}>
              {option}
            </label>
          </div>
        ))}
      </div>
    </div>
  );

  const SelectGroup = ({ title, options, field }: { title: string; options: string[]; field: keyof AssessmentData }) => (
    <div style={{ border: '1px solid #ccc', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
      <h3 style={{ color: '#059669', marginBottom: '12px', fontSize: '18px', fontWeight: 'bold' }}>{title}</h3>
      <select 
        value={data[field] as string} 
        onChange={(e) => updateField(field, e.target.value)}
        style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
      >
        <option value="">Auswählen...</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: '#374151', marginBottom: '8px' }}>
          Psychopathologischer Befund
        </h1>
        <p style={{ color: '#6b7280' }}>Strukturierte Dokumentation nach AMDP-Kriterien</p>
      </div>

      {/* Allgemeine Angaben */}
      <div style={{ border: '1px solid #ccc', borderRadius: '8px', padding: '16px', marginBottom: '24px', backgroundColor: '#f9fafb' }}>
        <h2 style={{ color: '#374151', marginBottom: '16px', fontSize: '20px', fontWeight: 'bold' }}>Allgemeine Angaben</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Patient/in</label>
            <input
              type="text"
              value={data.patientName}
              onChange={(e) => updateField('patientName', e.target.value)}
              placeholder="Name des Patienten"
              style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Geburtsdatum</label>
            <input
              type="date"
              value={data.dateOfBirth}
              onChange={(e) => updateField('dateOfBirth', e.target.value)}
              style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Untersuchungsdatum</label>
            <input
              type="date"
              value={data.assessmentDate}
              onChange={(e) => updateField('assessmentDate', e.target.value)}
              style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Untersucher/in</label>
            <input
              type="text"
              value={data.examiner}
              onChange={(e) => updateField('examiner', e.target.value)}
              placeholder="Name des Untersuchers"
              style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>
        </div>
      </div>

      {/* 1. Allgemeiner Eindruck */}
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e40af', marginBottom: '16px' }}>
          1. Allgemeiner Eindruck
        </h2>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
          <CheckboxGroup 
            title="Äußeres Erscheinungsbild" 
            options={assessmentOptions.appearance} 
            field="appearance" 
          />
          
          <CheckboxGroup 
            title="Kontaktverhalten" 
            options={assessmentOptions.contactBehavior} 
            field="contactBehavior" 
          />
          
          <CheckboxGroup 
            title="Sprache" 
            options={assessmentOptions.language} 
            field="language" 
          />
          
          <SelectGroup 
            title="Intelligenz" 
            options={assessmentOptions.intelligence} 
            field="intelligence" 
          />
        </div>
      </div>

      {/* 2. Bewusstseins- und Orientierungsstörungen */}
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#059669', marginBottom: '16px' }}>
          2. Bewusstseins- und Orientierungsstörungen
        </h2>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
          <CheckboxGroup 
            title="Qualitative Bewusstseinsstörungen" 
            options={assessmentOptions.qualitativeConsciousness} 
            field="qualitativeConsciousness" 
          />
          
          <CheckboxGroup 
            title="Quantitative Bewusstseinsstörungen" 
            options={assessmentOptions.quantitativeConsciousness} 
            field="quantitativeConsciousness" 
          />
          
          <CheckboxGroup 
            title="Orientierungsstörungen (ZOSP)" 
            options={assessmentOptions.orientationDisorders} 
            field="orientationDisorders" 
          />
        </div>
      </div>

      {/* 3. Aufmerksamkeits- und Gedächtnisstörungen */}
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#ea580c', marginBottom: '16px' }}>
          3. Aufmerksamkeits- und Gedächtnisstörungen
        </h2>
        
        <CheckboxGroup 
          title="Aufmerksamkeits- und Gedächtnisstörungen" 
          options={assessmentOptions.attentionMemory} 
          field="attentionMemory" 
        />
      </div>

      {/* 4. Formale Denkstörungen */}
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc2626', marginBottom: '16px' }}>
          4. Formale Denkstörungen
        </h2>
        
        <CheckboxGroup 
          title="Formale Denkstörungen" 
          options={assessmentOptions.formalThinking} 
          field="formalThinking" 
        />
      </div>

      {/* 5. Inhaltliche Denkstörungen */}
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#7c3aed', marginBottom: '16px' }}>
          5. Inhaltliche Denkstörungen
        </h2>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
          <CheckboxGroup 
            title="Befürchtungen und Zwänge" 
            options={assessmentOptions.fearsCompulsions} 
            field="fearsCompulsions" 
          />
          
          <CheckboxGroup 
            title="Wahninhalte" 
            options={assessmentOptions.delusionalContent} 
            field="delusionalContent" 
          />
          
          <CheckboxGroup 
            title="Formale Wahnmerkmale" 
            options={assessmentOptions.formalDelusional} 
            field="formalDelusional" 
          />
        </div>
      </div>

      {/* 6. Wahrnehmungsstörungen */}
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#4338ca', marginBottom: '16px' }}>
          6. Wahrnehmungsstörungen
        </h2>
        
        <CheckboxGroup 
          title="Wahrnehmungsstörungen" 
          options={assessmentOptions.perceptionDisorders} 
          field="perceptionDisorders" 
        />
      </div>

      {/* 7. Ich-Störungen */}
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#db2777', marginBottom: '16px' }}>
          7. Ich-Störungen
        </h2>
        
        <CheckboxGroup 
          title="Ich-Störungen" 
          options={assessmentOptions.egoDisorders} 
          field="egoDisorders" 
        />
      </div>

      {/* 8. Affektive Störungen */}
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#0d9488', marginBottom: '16px' }}>
          8. Affektive Störungen
        </h2>
        
        <CheckboxGroup 
          title="Affektive Störungen" 
          options={assessmentOptions.affectiveDisorders} 
          field="affectiveDisorders" 
        />
      </div>

      {/* 9. Störungen von Antrieb und Psychomotorik */}
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#0891b2', marginBottom: '16px' }}>
          9. Störungen von Antrieb und Psychomotorik
        </h2>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
          <CheckboxGroup 
            title="Antriebsstörungen" 
            options={assessmentOptions.driveDisorders} 
            field="driveDisorders" 
          />
          
          <CheckboxGroup 
            title="Psychomotorische Störungen" 
            options={assessmentOptions.psychomotorDisorders} 
            field="psychomotorDisorders" 
          />
        </div>
      </div>

      {/* 10. Zirkadiane Besonderheiten */}
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#059669', marginBottom: '16px' }}>
          10. Zirkadiane Besonderheiten
        </h2>
        
        <CheckboxGroup 
          title="Zirkadiane Besonderheiten" 
          options={assessmentOptions.circadianFeatures} 
          field="circadianFeatures" 
        />
      </div>

      {/* 11. Weitere Zusatzmerkmale */}
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#475569', marginBottom: '16px' }}>
          11. Weitere Zusatzmerkmale (nach AMDP)
        </h2>
        
        <CheckboxGroup 
          title="Weitere Zusatzmerkmale" 
          options={assessmentOptions.additionalFeatures} 
          field="additionalFeatures" 
        />
      </div>

      {/* Zusätzliche Notizen */}
      <div style={{ border: '1px solid #ccc', borderRadius: '8px', padding: '16px', marginBottom: '24px', backgroundColor: '#f9fafb' }}>
        <h2 style={{ color: '#374151', marginBottom: '16px', fontSize: '20px', fontWeight: 'bold' }}>Zusätzliche Notizen</h2>
        <textarea
          value={data.additionalNotes}
          onChange={(e) => updateField('additionalNotes', e.target.value)}
          placeholder="Weitere Beobachtungen oder spezielle Anmerkungen..."
          rows={4}
          style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', resize: 'vertical' }}
        />
      </div>

      {/* Generierter Befundbericht */}
      <div style={{ border: '2px solid #10b981', borderRadius: '8px', padding: '24px', backgroundColor: '#ecfdf5' }}>
        <h2 style={{ color: '#059669', marginBottom: '16px', fontSize: '20px', fontWeight: 'bold' }}>
          Generierter Befundbericht
        </h2>
        <textarea
          value={generateReport()}
          readOnly
          style={{ 
            width: '100%', 
            height: '400px', 
            padding: '12px', 
            border: '1px solid #ccc', 
            borderRadius: '4px', 
            backgroundColor: '#f9fafb',
            fontFamily: 'monospace',
            fontSize: '14px',
            lineHeight: '1.5',
            resize: 'vertical'
          }}
          placeholder="Der Befundbericht wird hier automatisch generiert..."
        />
        <div style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
          <button 
            onClick={copyToClipboard}
            style={{
              backgroundColor: '#059669',
              color: 'white',
              padding: '12px 24px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '500'
            }}
          >
            📋 Text kopieren
          </button>
          <button 
            onClick={openGPTChatbot}
            style={{
              backgroundColor: '#2563eb',
              color: 'white',
              padding: '12px 24px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '500'
            }}
          >
            🤖 Mit KI überarbeiten
          </button>
        </div>
      </div>
    </div>
  );
}